import { getPortfolios, orEmptyArray } from '@lms/api-client/backend';
import { Card, CardContent, CardDescription, CardHeader } from '@lms/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@lms/ui/table';
import { LoaderCircle } from 'lucide-react';
import useSWR from 'swr';
import { useTranslations } from 'use-intl';
import {
  PageBox,
  PageButtons,
  PageHeader,
  PageTitle,
} from '@/components/admin/page-box';
import { useSiteAccess } from '@/features/auth/use-auth';
import { useBrand } from '@/features/branding/brand-provider';
import { AddPortfolio } from '@/features/portfolios/components/add-portfolio';
import { OverflowMenu } from '@/features/portfolios/components/overflow-menu';

// Ports app/[brand]/admin/portfolios/page.tsx. Super-admin only: the Next app
// only surfaced this section to super-admins via the sidebar; here we gate the
// page itself and render a not-authorized notice otherwise. Data is loaded with
// SWR (replacing the server component's await) and mutated after add/remove.
export function Portfolios() {
  const t = useTranslations('admin');
  const { slug } = useBrand();
  const { access, loading: roleLoading } = useSiteAccess(slug);

  const {
    data: portfolios,
    isLoading,
    mutate,
  } = useSWR(access?.superAdmin ? ['admin/portfolios'] : null, () =>
    orEmptyArray(getPortfolios()),
  );

  if (roleLoading) {
    return (
      <div className="flex h-full items-center justify-center py-16">
        <LoaderCircle className="text-primary size-12 animate-spin" />
      </div>
    );
  }

  if (!access?.superAdmin) {
    return (
      <PageBox>
        <PageHeader>
          <PageTitle>{t('portfolios')}</PageTitle>
        </PageHeader>
        <p className="text-muted-foreground">{t('unauthorized')}</p>
      </PageBox>
    );
  }

  return (
    <PageBox>
      <PageHeader>
        <PageTitle>{t('portfolios')}</PageTitle>
        <PageButtons>
          <AddPortfolio onAdded={() => mutate()} />
        </PageButtons>
      </PageHeader>
      <Card>
        <CardHeader>
          <CardDescription>
            {t('manage', {
              what: t('portfolios').toLowerCase(),
            })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16" />
                <TableHead>{t('name')}</TableHead>
                <TableHead>{t('slug')}</TableHead>
                <TableHead className="w-5" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <div className="flex items-center justify-center py-8">
                      <LoaderCircle className="text-primary size-6 animate-spin" />
                    </div>
                  </TableCell>
                </TableRow>
              ) : portfolios && portfolios.length > 0 ? (
                portfolios.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>
                      <img
                        src={p.logo}
                        alt="logo"
                        className="h-16 w-16 object-contain"
                      />
                    </TableCell>
                    <TableCell>{p.name}</TableCell>
                    <TableCell>{p.slug}</TableCell>
                    <TableCell className="text-right">
                      <OverflowMenu id={p.id} onDeleted={() => mutate()} />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className="text-muted-foreground py-8 text-center"
                  >
                    {t('none')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </PageBox>
  );
}
